export const loadScript = (
  src: string,
  {
    id,
    async = false,
    defer = false,
    verifier,
    timeout = 5000,
  }: {
    id?: string;
    async?: boolean;
    defer?: boolean;
    verifier?: () => boolean;
    timeout?: number;
  } = {},
): Promise<HTMLScriptElement | null> => {
  return new Promise((resolve, reject) => {
    let script: HTMLScriptElement | null = null;
    let isLoadingEnded = false;
    let isRejected = false;

    if (verifier) {
      let elapsedTime = 0;
      const waitTime = 100;
      const timer = setInterval(() => {
        if (isLoadingEnded) {
          elapsedTime += waitTime;
        }

        if (verifier()) {
          clearInterval(timer);
          resolve(script);
          return;
        }

        if (isRejected || elapsedTime > timeout) {
          clearInterval(timer);
          reject();
        }
      }, waitTime);
    }

    script = id ? (window.document.getElementById(id) as HTMLScriptElement | null) : null;

    if (script) {
      script.remove();
      script = null;
    }

    script = window.document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = async;
    script.defer = defer;
    script.onerror = (err) => {
      isLoadingEnded = true;
      isRejected = true;
      reject(err);
    };
    script.onload = () => {
      isLoadingEnded = true;

      if (!verifier) {
        resolve(script as HTMLScriptElement);
      }
    };

    (script as typeof script & { onreadystatechange: string }).onreadystatechange = 'if (this.readyState === \'complete\') this.onload()';

    if (id) {
      script.id = id;
    }

    const scriptsList = window.document.getElementsByTagName('script');
    if (scriptsList && scriptsList.length) {
      scriptsList[0].parentNode?.insertBefore(script, scriptsList[0]);
    } else {
      window.document.head.appendChild(script);
    }
  });
};

export type LoadableScript = {
  src: string;
  options?: Parameters<typeof loadScript>[1];
};
